/**
 * Plaud OAuth Callback - handles Google OAuth redirect for Plaud integration
 * This page is opened in a popup and communicates back to the parent window
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CallbackStatus = 'processing' | 'success' | 'error';

export default function PlaudOAuthCallback() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Google OAuth with response_type=token returns the token in the URL fragment
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);

    const accessToken = params.get('access_token');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      setStatus('error');
      setErrorMessage(errorDescription || error);
      // Send error to parent
      if (window.opener) {
        window.opener.postMessage(
          { type: 'plaud-oauth-callback', error: errorDescription || error },
          window.location.origin
        );
      }
      return;
    }

    if (accessToken) {
      setStatus('success');
      // Send token to parent window
      if (window.opener) {
        window.opener.postMessage(
          { type: 'plaud-oauth-callback', accessToken },
          window.location.origin
        );
        // Close the popup after a brief delay
        setTimeout(() => window.close(), 500);
      } else {
        setErrorMessage(t('settings.plaudOAuth.couldNotCommunicate'));
        setStatus('error');
      }
    } else {
      setStatus('error');
      setErrorMessage(t('settings.plaudOAuth.noAccessToken'));
      if (window.opener) {
        window.opener.postMessage(
          { type: 'plaud-oauth-callback', error: 'No access token received' },
          window.location.origin
        );
      }
    }
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 dark:bg-neutral-900">
      <div className="w-full max-w-md space-y-6 rounded-lg bg-white p-8 text-center shadow-lg dark:bg-neutral-800">
        {status === 'processing' && (
          <>
            <div className="mx-auto h-12 w-12">
              <svg className="animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
              {t('settings.plaudOAuth.completing')}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t('settings.plaudOAuth.pleaseWait')}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
              {t('settings.plaudOAuth.connectedSuccessfully')}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t('settings.plaudOAuth.windowWillClose')}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
              {t('settings.plaudOAuth.connectionFailed')}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {errorMessage}
            </p>
            <button
              onClick={() => window.close()}
              className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              {t('settings.plaudOAuth.close')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
